/**
 * FeedbackDialog — lets clients rate and comment on a completed assessment.
 *
 * Persisted via POST /api/client/assessments/:id/feedback.
 * Shows existing feedback (read-only) if already submitted.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, TextField, Alert, Box, Rating, CircularProgress,
} from '@mui/material';
import { Feedback } from '@mui/icons-material';
import { authFetch } from '../api/authFetch';

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  assessmentId: string;
  assessmentDomain?: string;
}

const RATING_LABELS: Record<number, string> = {
  1: 'Poor',
  2: 'Fair',
  3: 'Good',
  4: 'Very Good',
  5: 'Excellent',
};

const FeedbackDialog: React.FC<FeedbackDialogProps> = ({
  open, onClose, assessmentId, assessmentDomain,
}) => {
  const [rating, setRating] = useState<number | null>(null);
  const [hoverRating, setHoverRating] = useState<number>(-1);
  const [comments, setComments] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [existingFeedback, setExistingFeedback] = useState<any>(null);

  useEffect(() => {
    if (!open || !assessmentId) return;
    setError(null);
    setSuccess(false);
    setLoading(true);

    authFetch(`/api/client/assessments/${encodeURIComponent(assessmentId)}/feedback`)
      .then((r) => r.json())
      .then((data) => {
        if (data.success && data.feedback) {
          setExistingFeedback(data.feedback);
          setRating(data.feedback.rating);
          setComments(data.feedback.comments || '');
        } else {
          setExistingFeedback(null);
          setRating(null);
          setComments('');
        }
      })
      .catch(() => { /* best effort */ })
      .finally(() => setLoading(false));
  }, [open, assessmentId]);

  const handleSubmit = async () => {
    if (!rating) {
      setError('Please select a rating.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await authFetch(`/api/client/assessments/${encodeURIComponent(assessmentId)}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, comments: comments.trim() || null }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccess(true);
        setExistingFeedback(data.feedback);
        setTimeout(() => onClose(), 1500);
      } else {
        setError(data.error || 'Failed to submit feedback.');
      }
    } catch {
      setError('Failed to submit feedback.');
    } finally {
      setSaving(false);
    }
  };

  const isReadOnly = !!existingFeedback;
  const activeLabel = RATING_LABELS[hoverRating !== -1 ? hoverRating : (rating || 0)] || '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Feedback fontSize="small" color="primary" />
        {isReadOnly ? 'Your Feedback' : 'Provide Feedback'}
        {assessmentDomain && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {assessmentDomain}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>Thank you for your feedback!</Alert>}

            {isReadOnly && (
              <Alert severity="info" sx={{ mb: 2 }}>
                You have already submitted feedback for this assessment.
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {isReadOnly
                ? 'Your submitted feedback is shown below.'
                : 'How would you rate this assessment experience? Your feedback helps us improve.'}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Rating
                value={rating}
                onChange={(_e, newValue) => {
                  if (!isReadOnly) setRating(newValue);
                }}
                onChangeActive={(_e, newHover) => {
                  if (!isReadOnly) setHoverRating(newHover);
                }}
                size="large"
                readOnly={isReadOnly}
                sx={{
                  fontSize: '2.5rem',
                  '& .MuiRating-iconFilled': { color: '#00338D' },
                }}
              />
              {activeLabel && (
                <Typography variant="body2" sx={{ mt: 1, color: '#00338D', fontWeight: 500 }}>
                  {activeLabel}
                </Typography>
              )}
            </Box>

            <TextField
              label="Comments (optional)"
              multiline
              rows={4}
              fullWidth
              value={comments}
              onChange={(e) => { if (!isReadOnly) setComments(e.target.value); }}
              disabled={isReadOnly}
              placeholder="Share your thoughts on the assessment process, report quality, or suggestions for improvement..."
              inputProps={{ maxLength: 2000 }}
              helperText={isReadOnly ? undefined : `${comments.length}/2000`}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{isReadOnly ? 'Close' : 'Cancel'}</Button>
        {!isReadOnly && !loading && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving || !rating}
          >
            {saving ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackDialog;
